import React, { useState, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import xlsx from "xlsx";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import responseHandler from "responseHandler";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import {
  MSpinner,
  MSplitter,
  MCard,
  MDialog,
  MGrid,
  MSelect,
  MDropzone,
  ActionButtons,
  MCheckboxGroup,
} from "components/Shared";
import { DATA_TYPE_DATE } from "typeConstants/dataTypes";
import RenderLicenses from "components/Appraiser/RenderLicenses";
import ExclusionaryList from "components/Shared/ExclusionaryList";
import { getAppraiserExclusionSearchResults, getCertificateList, getStateList } from "actions/appraiser";
import { getCustomersByType } from "requests/customer";
import { LENDER } from "typeConstants/customerTypes";
import { xlsxToWorkbook } from "components/Shared/Formatters/XLSX";
import { getExcludedAppraisers } from "requests/customerAppraiserExclusions";
import { getExcludedInspectors } from "requests/customerInspectorExclusions";
import { APPRAISER_CUSTOMER_EXCLUSIONS, FIELD_REP_CUSTOMER_EXCLUSIONS } from "typeConstants/paths";
import { getFieldRepExclusionSearchResults } from "actions/homeowner/fieldReps";

const { EXCEL } = MDropzone.fileTypes;
const APPRAISER = "Appraiser";
const FIELD_REP = "Field Rep";

const ExclusionSearch = ({
  fetchingAppraiserResults,
  appraiserResults,
  getAppraiserSearchResults,
  makeToast,
  getCertificateList,
  getStateList,
  stateList,
  certificateList,
  location,
  getFieldRepSearchResults,
  fetchingFieldRepResults,
  fieldRepResults,
}) => {
  const [showProfilePanel, setShowProfilePanel] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [{ clear, files, errors }, setDropzoneFunctions] = useState({ clear: () => {}, files: [], errors: [] });
  const [fileToUpload, setFileToUpload] = useState(null);
  const [showTodaysMatches, setShowTodaysMatches] = useState(false);
  const [showExistingSearchDialog, setShowExistingSearchDialog] = useState(false);
  const [existingResults, setExistingResults] = useState(null);
  const [selectedCustomerNumber, setSelectedCustomerNumber] = useState(null);
  const [exclusionRole, setExclusionRole] = useState();
  const customers = useRef([]);
  const mounted = useRef(true);
  const checkboxControlMethods = useRef({});
  const selectedCustomerName = useRef("");

  useEffect(() => {
    switch (location.pathname) {
      case `/${APPRAISER_CUSTOMER_EXCLUSIONS}`:
        setExclusionRole(APPRAISER);
        break;
      case `/${FIELD_REP_CUSTOMER_EXCLUSIONS}`:
        setExclusionRole(FIELD_REP);
        break;
    }
    getCustomersByType(
      LENDER,
      (response) => {
        customers.current = response;
      },
      mounted,
    );
    getStateList();
    getCertificateList();
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const parseWorkbook = async () => {
      const workbook = await xlsxToWorkbook(files[0].file);
      setFileToUpload(workbook);
    };
    if (files[0]) {
      parseWorkbook();
    }
  }, [files.length]);

  useEffect(() => {
    if (errors.length) {
      for (let error of errors) {
        makeToast(error, "error");
      }
    }
  }, [errors]);

  let searchResults =
    exclusionRole === APPRAISER ? appraiserResults : exclusionRole === FIELD_REP ? fieldRepResults : [];

  // Grid
  const getColumns = () => {
    const columns = [
      { key: "firstName", name: "First Name", dataSource: "firstName" },
      { key: "lastName", name: "Last Name", dataSource: "lastName" },
      { key: "number", name: "Number", dataSource: "number" },
    ];
    if (!existingResults) {
      columns.push(
        { key: "matchType", name: "Match Type", dataSource: "matchType" },
        { key: "actionNeeded", name: "Action Needed", dataSource: "actionNeeded" },
      );
    }
    if (exclusionRole === APPRAISER) {
      columns.push({
        key: "createdDate",
        name: "Date Added - Mueller",
        dataSource: "createdDate",
        dataType: DATA_TYPE_DATE,
      });
    }
    if (exclusionRole === FIELD_REP || !existingResults) {
      let dateColumnName = exclusionRole === APPRAISER ? "Date Added - Customer" : "Date Added";
      columns.push({
        key: "statusChangeDate",
        name: dateColumnName,
        dataSource: "statusChangeDate",
        dataType: DATA_TYPE_DATE,
      });
    }
    return columns;
  };
  const getRows = () => {
    if (existingResults) {
      switch (exclusionRole) {
        case APPRAISER:
          return existingResults.map((r) => {
            return {
              id: r.appraiser.id,
              firstName: r.appraiser.firstName,
              lastName: r.appraiser.lastName,
              number: r.appraiser.number,
              createdDate: r.createdDate,
            };
          });
        case FIELD_REP:
          return existingResults.map((r) => {
            return {
              id: r.inspector.id,
              firstName: r.inspector.firstName,
              lastName: r.inspector.lastName,
              number: r.inspector.number,
              statusChangeDate: r.statusChangeDate,
            };
          });
      }
    }
    if (!searchResults?.matches?.length) {
      return [];
    }
    let matches;
    switch (exclusionRole) {
      case APPRAISER:
        matches = searchResults?.matches?.map((m) => {
          return {
            id: m.appraiser.id,
            firstName: m.appraiser.firstName,
            lastName: m.appraiser.lastName,
            number: m.appraiser.number,
            matchType: getAppraiserMatchTypeLabel(m.matchType),
            actionNeeded: getActionNeeded(m.actionNeeded),
            statusChangeDate: m.customerStatusChangeDate,
            createdDate: m.createdDate,
          };
        });
        break;
      case FIELD_REP:
        matches = searchResults?.matches?.map((m) => {
          return {
            id: m.inspector.id,
            firstName: m.inspector.firstName,
            lastName: m.inspector.lastName,
            number: m.inspector.number,
            matchType: getInspectorMatchTypeLabel(m.matchType),
            actionNeeded: getActionNeeded(m.actionNeeded),
            statusChangeDate: m.statusChangeDate,
          };
        });
        break;
    }
    if (showTodaysMatches) {
      const today = new Date().toISOString().slice(0, 10);
      matches = matches.filter((m) => m.statusChangeDate.substring(0, 10) === today);
    }
    return matches;
  };
  // AppraiserExclusionMatchTypeEnum
  const appraiserMatchTypes = [
    "Last Name",
    "First and Last Name",
    "License Number",
    "License and Last Name",
    "Removed From List",
  ];
  const getAppraiserMatchTypeLabel = (matchTypeEnum) => {
    return appraiserMatchTypes[matchTypeEnum];
  };
  // InspectorExclusionMatchTypeEnum
  const inspectorMatchTypes = ["Last Name", "First and Last Name"];
  const getInspectorMatchTypeLabel = (matchTypeEnum) => {
    return inspectorMatchTypes[matchTypeEnum];
  };
  // ExclusionActionNeededEnum
  const getActionNeeded = (actionNeededEnum) => {
    const actions = ["None", "Add Exclusion", "Remove Exclusion", "Deficiency Review"];
    return actions[actionNeededEnum];
  };
  const onRowClick = (rowIndex) => {
    let row;
    if (existingResults) {
      row = existingResults[rowIndex];
    } else {
      row = searchResults.matches[rowIndex];
    }
    setSelectedUser(exclusionRole === APPRAISER ? row.appraiser : row.inspector);
    setShowProfilePanel(true);
  };
  const closeProfilePanel = () => setShowProfilePanel(false);
  const getGridTitle = () =>
    existingResults
      ? `Current Exclusions for ${selectedCustomerName.current}`
      : `${exclusionRole}s Matched for ${selectedCustomerName.current}`;
  const getGridConfigId = () => {
    switch (exclusionRole) {
      case APPRAISER:
        return existingResults ? "AppraiserExclusionViewExisting" : "AppraiserExclusionSearch";
      case FIELD_REP:
        return existingResults ? "FieldRepExclusionViewExisting" : "FieldRepExclusionSearch";
    }
  };

  // Upload Dialog
  const toggleUploadDialog = () => {
    setShowUploadDialog(!showUploadDialog);
    setSelectedCustomerNumber(null);
  };
  const uploadDialogActions = [
    {
      disabled: files.length === 0 || errors.length > 0 || !selectedCustomerNumber,
      onClick: () => upload(),
      children: "Upload",
    },
    { disabled: files.length === 0, onClick: () => reset(), children: "Reset" },
    { onClick: toggleUploadDialog, children: "Cancel" },
  ];
  const upload = () => {
    // Need to clear these here so we can show the search results
    setExistingResults(null);
    const file = new Blob([xlsx.write(fileToUpload, { bookType: "xlsx", bookSST: "false", type: "array" })]);
    const matchTypes = checkboxControlMethods.current.getChecked();
    switch (exclusionRole) {
      case APPRAISER:
        getAppraiserSearchResults(selectedCustomerNumber, file, matchTypes);
        break;
      case FIELD_REP:
        getFieldRepSearchResults(selectedCustomerNumber, file, matchTypes);
        break;
    }
    toggleUploadDialog();
  };
  const reset = () => {
    clear();
    setFileToUpload(null);
  };
  const onCustomerChange = (option) => {
    setSelectedCustomerNumber(option.value);
    selectedCustomerName.current = option.label;
  };
  const getPortalTarget = () => document.querySelector("#card-dialog");
  const memoizedGetMatchTypes = useMemo(() => {
    let matchTypes = [];
    switch (exclusionRole) {
      case APPRAISER:
        matchTypes = appraiserMatchTypes;
        break;
      case FIELD_REP:
        matchTypes = inspectorMatchTypes;
        break;
    }
    return matchTypes
      .map((label, index) => ({
        value: index,
        label: label,
        defaultChecked: index > 0,
      }))
      .reverse();
  }, [exclusionRole]);

  // Existing Exclusions Search Dialog
  const toggleExistingDialog = () => {
    setShowExistingSearchDialog(!showExistingSearchDialog);
    setSelectedCustomerNumber(null);
  };
  const existingDialogActions = [
    { disabled: !selectedCustomerNumber, onClick: () => searchExisting(), children: "Search" },
    { onClick: toggleExistingDialog, children: "Cancel" },
  ];
  const searchExisting = () => {
    switch (exclusionRole) {
      case APPRAISER:
        getExcludedAppraisers(selectedCustomerNumber, handleExistingResults, mounted);
        break;
      case FIELD_REP:
        getExcludedInspectors(selectedCustomerNumber, handleExistingResults, mounted);
        break;
    }
    toggleExistingDialog();
  };
  const handleExistingResults = (results) => {
    let exclusionsFromResults;
    switch (exclusionRole) {
      case APPRAISER:
        exclusionsFromResults = results
          .map((o) => o.appraisersExclusionList)
          .reduce((acc, val) => acc.concat(val), [])
          .map((o) => {
            return {
              appraiser: o.appraiser,
              createdDate: o.createdDate,
            };
          });
        break;
      case FIELD_REP:
        exclusionsFromResults = results
          .map((o) => o.inspectorsExclusionList)
          .reduce((acc, val) => acc.concat(val), [])
          .map((o) => {
            return {
              inspector: o.inspectors,
              statusChangeDate: o.createDate,
            };
          });
        break;
    }
    setExistingResults(exclusionsFromResults);
  };

  // Search results filter buttons
  const showToday = () => {
    setShowTodaysMatches(true);
    setExistingResults(null);
  };
  const showAll = () => {
    setShowTodaysMatches(false);
    setExistingResults(null);
  };

  const cellWidth = 12; // Only displaying this vertically

  return (
    <MSplitter
      firstChild={
        <div className="padding-page-content full-height">
          <Card className="full-height" sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
            {(fetchingAppraiserResults || fetchingFieldRepResults) && <MSpinner />}
            {!fetchingAppraiserResults && !fetchingFieldRepResults && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    "& button": { m: "var(--margin-10px)" },
                    "& button:last-of-type": { ml: "auto" },
                  }}
                >
                  <Button onClick={toggleUploadDialog}>Upload List</Button>
                  <Button onClick={showToday} disabled={!searchResults?.matches?.length || showTodaysMatches}>
                    Today's Changes
                  </Button>
                  <Button onClick={showAll} disabled={!searchResults?.matches?.length || !showTodaysMatches}>
                    Show All
                  </Button>
                  <Button onClick={toggleExistingDialog}>Search Existing</Button>
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <MGrid
                    configurationId={getGridConfigId()}
                    headerText={getGridTitle()}
                    columnDefinitions={getColumns()}
                    data={getRows()}
                    isFetchingData={fetchingAppraiserResults || fetchingFieldRepResults}
                    draggableColumns
                    onRowClick={onRowClick}
                    displayNumOfItemsFound
                  />
                </Box>
                {(showUploadDialog || showExistingSearchDialog) && (
                  <MDialog
                    title={
                      showUploadDialog ? "Upload Customer Exclusion List" : "Search Existing Exclusions by Customer"
                    }
                    visible={showUploadDialog || showExistingSearchDialog}
                    focusOnMount={false}
                    sx={{ width: 525 }}
                  >
                    {!customers.current.length && <MSpinner />}
                    {customers.current.length > 0 && (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
                        <p>Customer:</p>
                        <MSelect
                          isClearable={false}
                          label="Customer"
                          itemLabel="name"
                          itemValue="id"
                          options={customers.current.map((x) => ({ label: x.displayLabel, value: x.number }))}
                          onChange={onCustomerChange}
                          enablePortal
                          getMenuPortalTarget={getPortalTarget}
                        />
                        {showUploadDialog && (
                          <>
                            <p>Search match types:</p>
                            <MCheckboxGroup
                              controlMethods={(cm) => (checkboxControlMethods.current = cm)}
                              fields={memoizedGetMatchTypes}
                              cellSize={6}
                              containerClassName={"exclusion-match-types-checkbox-container"}
                            />
                            <MDropzone
                              className={"exclusion-dropzone"}
                              setDropzoneProperties={setDropzoneFunctions}
                              onFileAdd={() => {}}
                              onFileRemove={() => setFileToUpload(null)}
                              prompt="Click here to choose a file to upload"
                              validFileTypes={{
                                type: EXCEL,
                              }}
                              trackFiles
                            />
                          </>
                        )}
                        <ActionButtons
                          buttonPropSets={showUploadDialog ? uploadDialogActions : existingDialogActions}
                        />
                      </Box>
                    )}
                  </MDialog>
                )}
              </>
            )}
          </Card>
        </div>
      }
      secondChild={
        showProfilePanel && (
          <Grid container spacing={1}>
            <Grid size={cellWidth}>
              <MCard
                title={exclusionRole}
                fields={[
                  { key: "selectedUserNumber", label: `${exclusionRole} #`, value: selectedUser.number },
                  { key: "firstName", label: "First Name", value: selectedUser.firstName },
                  { key: "lastName", label: "Last Name", value: selectedUser.lastName },
                ]}
              />
            </Grid>
            {exclusionRole === APPRAISER && (
              <Grid size={cellWidth}>
                <MCard
                  title="License and Certification"
                  fields={[
                    {
                      key: "licenseand",
                      control: (
                        <RenderLicenses
                          certificateList={certificateList}
                          stateList={stateList}
                          appraiserNumber={selectedUser.number}
                        />
                      ),
                    },
                  ]}
                />
              </Grid>
            )}
            <Grid size={cellWidth}>
              <MCard
                title="Exclusionary List"
                fields={[
                  {
                    key: "exclusions",
                    control: (
                      <ExclusionaryList
                        appraiserNumber={exclusionRole === APPRAISER ? selectedUser.number : null}
                        inspectorNumber={exclusionRole === FIELD_REP ? selectedUser.number : null}
                      />
                    ),
                  },
                ]}
              />
            </Grid>
          </Grid>
        )
      }
      secondChildTitle={{
        title: `${exclusionRole} Info`,
        buttons: [{ icon: "close", onClick: closeProfilePanel }],
      }}
    />
  );
};

ExclusionSearch.propTypes = {
  fetchingAppraiserResults: PropTypes.bool,
  appraiserResults: PropTypes.object,
  getAppraiserSearchResults: PropTypes.func,
  makeToast: PropTypes.func,
  getCertificateList: PropTypes.func,
  getStateList: PropTypes.func,
  stateList: PropTypes.array,
  certificateList: PropTypes.array,
  location: PropTypes.object,
  getFieldRepSearchResults: PropTypes.func,
  fetchingFieldRepResults: PropTypes.bool,
  fieldRepResults: PropTypes.object,
};

const mapStateToProps = (state) => ({
  fetchingAppraiserResults: state.appraiser.fetchingExclusionSearchResults,
  appraiserResults: state.appraiser.exclusionSearchResults,
  certificateList: state.appraiser.certificateList,
  stateList: state.appraiser.stateList,
  fetchingFieldRepResults: state.fieldRep.fetchingExclusionSearchResults,
  fieldRepResults: state.fieldRep.exclusionSearchResults,
});

const mapDispatchToProps = (dispatch) => ({
  getAppraiserSearchResults(customerNumber, file, matchTypes) {
    dispatch(getAppraiserExclusionSearchResults(customerNumber, file, matchTypes));
  },
  makeToast(text, type) {
    responseHandler.customToast(text, type, dispatch);
  },
  getCertificateList() {
    dispatch(getCertificateList());
  },
  getStateList() {
    dispatch(getStateList());
  },
  getFieldRepSearchResults(customerNumber, file, matchTypes) {
    dispatch(getFieldRepExclusionSearchResults(customerNumber, file, matchTypes));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ExclusionSearch);
